import { PlusCircleOutlined } from '@ant-design/icons'
import { Button, DatePicker, Menu, message, Modal, Spin } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AccessControlAction from '../../components/AccessControlAction'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import { systemName } from '../../config'
import {
  ACL_ACTION_TYPE,
  DATE_FORMAT_DEFAULT,
  ISO_DATE_FORMAT,
} from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout'
import { DocumentAccessibilityRequest } from '../../requests/DocumentAccessibilityRequest'
import { WorkScheduleRequest } from '../../requests/WorkScheduleRequest'
import OfficeCalendar from './OfficeCalendar'
import queryString from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'
import { OfficeCalendarEventDetail } from './OfficeCalendarEventDetail'
import AppPageHeader from '../../components/AppPageHeader'

const CompanyWorkScheduleListPageV2 = props => {
  const { authenticationStore, companyStore } = props

  const { currentUser } = authenticationStore
  const { companyList } = companyStore

  const history = useHistory()
  const location = useLocation()

  // Parse query params từ URL
  const queryParams = useMemo(
    () =>
      queryString.parse(location.search, {
        arrayFormat: 'index',
      }),
    [location.search]
  )

  // Lấy giá trị từ URL query, fallback về default nếu chưa có
  const selectedDate = queryParams.date || moment().format(ISO_DATE_FORMAT)

  const selectedKey = queryParams.company || currentUser?.company?.code || ''

  // Hàm cập nhật URL query params
  const updateQueryParams = useCallback(
    newParams => {
      const currentParams = queryString.parse(location.search, {
        arrayFormat: 'index',
      })
      const merged = { ...currentParams, ...newParams }
      history.replace({
        search: queryString.stringify(merged, { arrayFormat: 'index' }),
      })
    },
    [location.search, history]
  )

  const [isFetchingData, setIsFetchingData] = useState(false)
  const [isSubmittingAction, setIsSubmittingAction] = useState(false)

  const [weeklyWorkSchedule, setWeeklyWorkSchedule] = useState([])
  const [workScheduleList, setWorkScheduledList] = useState([])
  const [loadedWeekStart, setLoadedWeekStart] = useState(null)
  const [loadedCompany, setLoadedCompany] = useState(null)

  const [workSchedulePermissionView, setWorkSchedulePermissionView] = useState(
    []
  )

  const [workScheduleDetail, setWorkScheduleDetail] = useState(null)

  // selectedEvent được quản lý qua URL query param `eventId`
  const selectedEventId = queryParams.eventId || null

  const handleSelectEvent = useCallback(
    event => {
      updateQueryParams({ eventId: event?.id ?? '' })
    },
    [updateQueryParams]
  )

  // Sync company vào URL khi lần đầu load (nếu chưa có trong URL)
  useEffect(() => {
    if (!queryParams.company && currentUser?.company?.code) {
      updateQueryParams({ company: currentUser.company.code })
    }
  }, [currentUser?.company?.code]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync date vào URL khi lần đầu load (nếu chưa có trong URL)
  useEffect(() => {
    if (!queryParams.date) {
      updateQueryParams({ date: moment().format(ISO_DATE_FORMAT) })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetWorkSchedulePermissionView = useCallback(async () => {
    try {
      setIsFetchingData(true)
      const response = await DocumentAccessibilityRequest.getDocumentAccessibilities(
        'WORK_SCHEDULE',
        ''
      )
      setWorkSchedulePermissionView(response?.data || [])
    } catch (error) {
      setWorkSchedulePermissionView([])
    } finally {
      setIsFetchingData(false)
    }
  }, [])

  const handleGetWorkSchedule = useCallback(async (forceRefresh = false) => {
    if (!selectedDate || !selectedKey) return

    const startOfWeek = moment(selectedDate).startOf('isoWeek').format(ISO_DATE_FORMAT)
    const endOfWeek = moment(selectedDate).endOf('isoWeek').format(ISO_DATE_FORMAT)

    if (!forceRefresh && loadedWeekStart === startOfWeek && loadedCompany === selectedKey) {
      const activeDayData = weeklyWorkSchedule.filter(item =>
        moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
      )
      setWorkScheduledList(activeDayData)
      return
    }

    try {
      setIsFetchingData(true)
      const response = await WorkScheduleRequest.getWorkSchedule(
        startOfWeek,
        endOfWeek,
        true,
        selectedKey
      )

      const allData = response?.data || []
      setWeeklyWorkSchedule(allData)
      setLoadedWeekStart(startOfWeek)
      setLoadedCompany(selectedKey)

      const activeDayData = allData.filter(item =>
        moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
      )
      setWorkScheduledList(activeDayData)
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
      setWeeklyWorkSchedule([])
      setWorkScheduledList([])
      setLoadedWeekStart(null)
      setLoadedCompany(null)
    } finally {
      setIsFetchingData(false)
    }
  }, [selectedDate, selectedKey, loadedWeekStart, loadedCompany, weeklyWorkSchedule])

  const handleGetWorkScheduleDetail = useCallback(async () => {
    if (!selectedEventId) return
    try {
      setIsFetchingData(true)
      const response = await WorkScheduleRequest.getWorkScheduleDetail(
        selectedEventId
      )

      setWorkScheduleDetail(response?.data || null)
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
      setWorkScheduleDetail(null)
    } finally {
      setIsFetchingData(false)
    }
  }, [selectedEventId])

  useEffect(() => {
    handleGetWorkSchedulePermissionView()
  }, [handleGetWorkSchedulePermissionView])

  useEffect(() => {
    handleGetWorkSchedule()
  }, [handleGetWorkSchedule])

  useEffect(() => {
    handleGetWorkScheduleDetail()
  }, [handleGetWorkScheduleDetail])

  // Khi user chọn ngày → cập nhật URL
  const handleChangeDate = useCallback(
    (date) => {
      setWorkScheduleDetail(null)
      updateQueryParams({ date: date?.format(ISO_DATE_FORMAT) })
    },
    [updateQueryParams]
  )

  // Khi user chọn công ty → cập nhật URL
  const changeCategory = useCallback(
    e => {
      updateQueryParams({ company: e.key })
    },
    [updateQueryParams]
  )

  const handleDeleteEvent = async schedule_code => {
    Modal.confirm({
      title: 'Xóa sự kiện',
      content: 'Bạn có chắc chắn muốn xóa sự kiện này?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setIsSubmittingAction(true)
          await WorkScheduleRequest.delete(schedule_code)
          message.success('Xóa sự kiện thành công!')
          updateQueryParams({ eventId: '' })
          setWorkScheduleDetail(null)
          handleGetWorkSchedule(true)
        } catch (err) {
          console.log(err)
          message.error(err.vi || 'Đã có lỗi xảy ra!')
        } finally {
          setIsSubmittingAction(false)
        }
      },
      okButtonProps: {
        danger: true,
        loading: isSubmittingAction,
      },
      cancelButtonProps: {
        disabled: isSubmittingAction,
      },
    })
  }

  const handleEditEvent = schedule_code => {
    history.push({
      pathname: `/company-work-schedule/view/${schedule_code}`,
      search: `?mode=edit_form&date=${selectedDate}&company=${selectedKey}`,
    })
  }

  const renderMenuItems = () => {
    let currentUserCompanyMenuItem = null
    const menuItems = companyList.map(company => {
      const matchingPermission = workSchedulePermissionView.find(
        permission => permission.access_type === company.code
      )

      if (matchingPermission) {
        if (
          company.code === currentUser?.company.code &&
          !currentUserCompanyMenuItem
        ) {
          // MenuItem currentUser's company
          currentUserCompanyMenuItem = (
            <Menu.Item key={company.code}>{company.name}</Menu.Item>
          )
        } else {
          // MenuItem another company
          return <Menu.Item key={company.code}>{company.name}</Menu.Item>
        }
      }
      return null
    })

    if (currentUserCompanyMenuItem) {
      // set currentUser's company to the first of list
      menuItems.unshift(currentUserCompanyMenuItem)
    }

    return menuItems
  }

  const isAccessCreate = selectedKey === currentUser?.company.code

  return (
    <DashboardLayout title={'Link People - Link the World'}>
      <Helmet>
        <title>Lịch cơ quan | {systemName} Portal</title>
      </Helmet>
      <AppPageHeader
        title="Lịch cơ quan"
        addon={
          <>
            <DatePicker
              picker="week"
              onChange={handleChangeDate}
              placeholder={'Chọn tuần'}
              format={date => `Tuần ${date.isoWeek()}, ${date.year()}`}
              value={moment(selectedDate)}
              style={{ height: 32, width: 200 }}
              allowClear={false}
            />
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.create__WORK_SCHEDULE}>
              <Button
                style={{ marginLeft: 15 }}
                onClick={() => history.push('/company-work-schedule/create')}
                type={'primary'}
                disabled={!isAccessCreate}>
                <PlusCircleOutlined />
                Tạo sự kiện mới
              </Button>
            </AccessControlAction>
          </>
        }
      />

      <Spin spinning={isFetchingData}>
        <ContentBlockWrapper style={{ minHeight: 600 }}>
          <Menu
            className="custom-menu-tab"
            mode="horizontal"
            selectedKeys={[selectedKey]}
            style={{ marginBottom: 16 }}
            onClick={changeCategory}>
            {renderMenuItems()}
          </Menu>
          <OfficeCalendar
            selectedDate={selectedDate}
            data={workScheduleList}
            selectedEventId={selectedEventId}
            onEventClick={handleSelectEvent}
            isShowPopoverContent={false}
            onChangeDate={handleChangeDate}
          />

          {Boolean(selectedEventId && workScheduleDetail) ? (
            <>
              <div style={{ height: 24 }} />
              <OfficeCalendarEventDetail
                onDeleteEvent={handleDeleteEvent}
                onEditEvent={handleEditEvent}
                workScheduleDetail={workScheduleDetail}
              />
            </>
          ) : null}
        </ContentBlockWrapper>
      </Spin>
    </DashboardLayout>
  )
}

export default inject(
  'authenticationStore',
  'companyStore'
)(observer(CompanyWorkScheduleListPageV2))
